import { useMemo } from 'react';

/**
 * Progress Chart Component
 * Reusable chart component for progress tracking
 * Simple implementation - can be enhanced with charting library later
 */
export default function ProgressChart({ data, type = 'line', xKey = 'date', yKey = 'value', label = 'Value' }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(item => ({
      x: item[xKey],
      y: item[yKey],
    }));
  }, [data, xKey, yKey]);

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        No data available for chart
      </div>
    );
  }

  // Simple SVG line chart implementation
  const maxY = Math.max(...chartData.map(d => d.y));
  const minY = Math.min(...chartData.map(d => d.y));
  const rangeY = maxY - minY || 1;
  const padding = 40;
  const width = 600;
  const height = 300;

  const points = chartData.map((point, index) => {
    const x = padding + (index / (chartData.length - 1 || 1)) * (width - padding * 2);
    const y = height - padding - ((point.y - minY) / rangeY) * (height - padding * 2);
    return { x, y, value: point.y, date: point.x };
  });

  const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <div className="w-full overflow-x-auto">
      <svg width={width} height={height} className="w-full h-auto">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - padding - ratio * (height - padding * 2);
          const value = minY + ratio * rangeY;
          return (
            <g key={ratio}>
              <line
                x1={padding}
                y1={y}
                x2={width - padding}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
              <text
                x={padding - 10}
                y={y + 4}
                textAnchor="end"
                className="text-xs fill-gray-600"
              >
                {value.toFixed(1)}
              </text>
            </g>
          );
        })}

        {/* Chart line */}
        {type === 'line' && (
          <path
            d={pathData}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {/* Data points */}
        {points.map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill="#3b82f6"
              className="hover:r-6 transition-all"
            />
            <title>
              {label}: {point.value} on {new Date(point.date).toLocaleDateString()}
            </title>
          </g>
        ))}

        {/* X-axis labels */}
        {points.length > 0 && points.length <= 7 && (
          points.map((point, index) => {
            if (index % Math.ceil(points.length / 5) === 0 || index === points.length - 1) {
              return (
                <text
                  key={index}
                  x={point.x}
                  y={height - padding + 20}
                  textAnchor="middle"
                  className="text-xs fill-gray-600"
                >
                  {new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </text>
              );
            }
            return null;
          })
        )}
      </svg>
    </div>
  );
}
